// src/hooks/insights/useInsightsOrgCode.ts
//
// Resolves the org code the Insights tab should query. `OrganizationProvider` itself
// reads `useUserUpdaterProvider().orgUser.org_code` for its own config fetches (see
// OrganizationProvider.tsx), and orgUser is populated earlier than `organization`, which
// starts null - so this follows the same source rather than waiting on OrganizationProvider.
//
// Dev-only override: append `?insightsOrgCode=org-smoke` to the URL to point at the M0
// seed fixtures without a live user org. Gated on `import.meta.env.DEV`, which Vite
// statically replaces with the literal `false` in production builds - dead-code
// elimination then strips the whole branch, so the override is not reachable (or even
// present) in a production build.

import { useUserUpdaterProvider } from '@/context_providers/user_context/UserUpdaterProvider';

const DEV_ORG_CODE_QUERY_PARAM = 'insightsOrgCode';

function readDevOrgCodeOverride(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(DEV_ORG_CODE_QUERY_PARAM);
}

/** Returns the org code to query, or `''` when no org user is loaded yet. */
export function useInsightsOrgCode(): string {
  const { orgUser } = useUserUpdaterProvider();

  if (import.meta.env.DEV) {
    const override = readDevOrgCodeOverride();
    if (override) return override;
  }

  return orgUser?.org_code ?? '';
}
